import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon, Form } from 'tmt-ui-kit';
import xss from 'xss';
import moment from 'moment-timezone';

import Link from '~/components/Link/Link';
import RatingPoint from '~/components/General/RatingPoint';

import { path } from '~/utils/media';
import { ceil } from '~/utils/price';
import Colors from '~/utils/colors';
import { getProductUrl } from '~/utils/trip';
import { isMultiTier } from '~/utils/payment';
import { stripLink, commaizeInt } from '~/utils/helpers';
// import memoize from 'lodash/memoize';
// import config from '~/config';

const { Button } = Form;

const Card = styled.div`
  color: ${Colors.gray};
  display: flex;
  cursor: pointer;
  user-select: none;
  padding-bottom: 30px;
  padding-right: 10px;
  white-space: normal !important;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 700px) {
    width: 100%;
    padding-bottom: 10px;
    padding-left: 0;
    padding-right: 0;
    margin-top: 20px;
  }
`;

const CardThumbnail = styled.div`
  width: 30%;
  height: 220px;
  border-radius: 6px;
  background-size: cover;
  background-position: center center;
  background-image: url(${props => props.image});
  @media (max-width: 700px) {
    display: none;
  }
`;

const CardInfo = styled.div`
  position: relative;
  flex: 1;
  padding: 8px 16px 0;
  box-sizing: border-box;
  @media (max-width: 700px) {
    padding-left: 0;
  }
`;

const TripName = styled.h3`
  margin: 0;
  font-size: 20px;
  @media(max-width: 600px) {
    width: 80%;
  }
`;

const TripLocation = styled.p`
  color: ${Colors.lightGray};
`;

const Infos = styled.div`
  &::after {
    content: " ";
    display: block;
    height: 0;
    clear: both;
  }
  @media (max-width: 700px) {
    max-width: 400px;
  }
`;

const Info = styled.div`
  float: left;
  margin-right: 20px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  &:nth-child(n2+1) {
    clear: both;
  }
  >span {
    color: ${Colors.darkGreen};
    @media (max-width: 700px) {
      font-size: 13px;
    }
  }
  @media (max-width: 700px) {
    margin-right: 0;
    width: 50%;
  }
`;

const CheckIcon = styled(Icon)`
  display: inline-block;
  color: ${props => props.theme.green};
  margin-right: 5px;
  position: relative;
  /* color: #e36b25; */
  color: ${props => props.theme.greenLighter};
`;

const Clock = styled(Icon)`
  margin-right: 6px;
`;

const CloseButton = styled(Icon)`
  position: absolute;
  right: -20px;
  top: -20px;
  padding: 20px;
  z-index: 10;
  outline: none;
  user-select: none;
  cursor: pointer;
`;

const PointContainer = styled.div`
  cursor: pointer;
  margin: 10px 0;
  display: flex;
`;

const ReviewCountSpan = styled.div`
  margin-left: 5px;
`;

const OrangeButton = styled(Button.OrangeButton)`
  display: none;
  @media (max-width: 700px) {
    display: block;
  }
`;

const SubPrice = styled.span`
  /* text-align: right; */
  margin: 0 4px;
  color: ${props => props.theme.opacity.black};
  font-weight: 400;
  /* font-size: 10px; */
  margin-bottom: 3px;

  @media (max-width: 320px) {
    display: block;
  }
`;

const DiscountedPrice = styled.div`
  text-decoration: line-through;
  font-weight: 400;
  /* font-size: 10px; */
  color: ${props => props.theme.opacity.black};
  margin-bottom: -2px;
`;

const GrayPrice = styled.span`
  font-weight: 500;
  color: rgba(40,57,65,0.5) !important;
`;

const GreenPrice = styled.span`
  font-weight: 500;
  margin: 0 4px;
  color: ${props => props.theme.greenLighter} !important;
`;

// const GreenSpan = styled.span`
//   color: ${props => props.theme.greenLighter};
// `;

// const ArrowRight = styled.div`
//   width: 30px;
//   height: 30px;
//   background-repeat: no-repeat;
//   background-position: center center;
//   background-size: 30px 30px;
//   background-image:  url('${config.assetPath}/static/icons/chevron-right-grey.png');
//   position: absolute;
//   right: 0;
//   top: 30%;
//   @media (max-width: 700px) {
//     display: none;
//   }
// `;
const calculateProductPrice = (productData, quantity = 1, currency, rates) => {
  /*
    note:
    displayPrice is crossed price
    discountedPrice is real final price
  */
  if (productData.product_type === 'trip') {
    const prices = productData.full_prices.map(price => price.seasoned);
    const discountedPrices = productData.full_prices.map(price => price.seasonedDiscountedPerPerson);
    const realQuantity = (quantity > productData.max_travelers) ? productData.max_travelers : (quantity || 1);
    const displayPrice = commaizeInt(ceil(prices[realQuantity - 1], currency, rates));
    const discountedPrice = commaizeInt(ceil(discountedPrices[realQuantity - 1], currency, rates));
    return {
      displayPrice,
      discountedPrice
    };
  }
  if (productData.product_type === 'ticket') {
    let realPrice = 0;
    let crossPrice = 0;

    if (isMultiTier(productData)) {
      productData.multi_tier_prices.map(t => t.sub_tiers
        .filter(c => c.name !== 'Child')
        .map(st => {
          if (realPrice === 0) {
            realPrice = st.price;
            crossPrice = st.display_price;
          }
          if (st.price < realPrice) {
            realPrice = st.price;
            crossPrice = st.display_price;
          }
          return null;
        }));
    }
    const finalPrice = commaizeInt(ceil(realPrice, currency, rates));
    const crossDisplayPrice = commaizeInt(ceil(crossPrice, currency, rates));
    return {
      displayPrice: crossDisplayPrice,
      discountedPrice: finalPrice
    };
  }
  return 0;
};

const PriceDetail = ({ productType, data }) => {
  if (productType === 'trip') {
    const { time, displayPrice, discountedPrice, currency } = data;
    return (
      <Fragment>
        <Infos>
          <Info>
            <Clock name="clock-24" />
            {time}
          </Info>
        </Infos>
        {displayPrice > discountedPrice
        && (
          <Infos>
            <Info>
              <SubPrice>
                <DiscountedPrice><GrayPrice>{currency} {displayPrice}</GrayPrice></DiscountedPrice>
              </SubPrice>
            </Info>
          </Infos>
        )}
        <Infos>
          <Info>
            <GreenPrice>{currency} {discountedPrice}</GreenPrice> / person
          </Info>
        </Infos>
      </Fragment>
    );
  }
  if (productType === 'ticket') {
    const { displayPrice, discountedPrice, currency } = data;
    return (
      <Fragment>
        {displayPrice > discountedPrice
        && (
          <Infos>
            <Info>
              <SubPrice>
                <DiscountedPrice><GrayPrice>{currency} {displayPrice}</GrayPrice></DiscountedPrice>
              </SubPrice>
            </Info>
          </Infos>
        )}
        <Infos>
          <Info>
            <GreenPrice>{currency} {discountedPrice}</GreenPrice> / person
          </Info>
        </Infos>
      </Fragment>
    );
  }
  return null;
};

const HistoryCard = (props) => {
  const endTime = moment(props.data.starting_time, 'HH:mm')
    .add(parseInt(props.data.duration, 10), 'hours')
    .format('HH:mm');
  const time = `${props.data.starting_time} - ${endTime}`;

  const { quantity, currency } = props.sessionData;
  const prices = calculateProductPrice(props.data, quantity, currency, props.rates);
  return (
    <Link to={props.data.slug ? getProductUrl(props.data) : '/'}>
      <Card>
        <CardThumbnail image={path(props.data.cover_image, 'rectangle-800-600')} />
        <CardInfo>
          <CloseButton
            name="close"
            onClick={(e)=> {
              e.preventDefault();
              e.stopPropagation();
              props.onClose();
              return false;
            }}
          >
            &nbsp;
          </CloseButton>
          <TripName>{props.data.name}</TripName>
          <TripLocation>{props.data.destination_location}</TripLocation>
          {props.data.review_count > 0 && (
            <PointContainer>
              {props.data.user_id && props.data.user_id.rating && props.data.user_id.rating.average_rating > 0 && (
                <RatingPoint point={props.data.user_id.rating.average_rating}/>
              )}
              <ReviewCountSpan>({props.data.review_count} Reviews)</ReviewCountSpan>
            </PointContainer>
          )}
          <Infos>
            {['Private Car', 'Private Van'].indexOf(props.data.transportation) >= 0 && (
              <Info>
                <CheckIcon name="check" />
                Private Car
              </Info>
            )}
            {props.data.search_information && props.data.search_information.is_hotel_pickup && (
              <Info>
                <CheckIcon name="check" />
                Free hotel pickup
              </Info>
            )}
          </Infos>
          <PriceDetail productType={props.data.product_type} data={{ ...prices, time, currency}} />
          <p dangerouslySetInnerHTML={{ __html: xss(props.fullPath ? stripLink(props.data.introduction, props.fullPath) : props.data.introduction) }}></p>
        </CardInfo>
      </Card>
      <OrangeButton>More detail</OrangeButton>
    </Link>
  );
};

HistoryCard.propTypes = {
  data: PropTypes.object,
  fullPath: PropTypes.string,
  onClose: PropTypes.func,
};
HistoryCard.defaultProps = {
  data: {},
  onClose: () => ({}),
};

export default HistoryCard;
